import { useCallback, useState, useMemo } from 'react';
import {KeyboardArrowLeft, KeyboardArrowRight } from '@mui/icons-material';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';

import { DateTime } from 'luxon';
import Interval from 'luxon/src/interval.js';
import '../style/_Calendars.scss';

/**
 * Type validi sono: 'month', 'day', 'year'
 */
export function CardCalendar({ type, value, onChange, minDate, maxDate, closeCalendar }) {
  const interval = useMemo(() => Interval.fromDateTimes(minDate, maxDate), [minDate, maxDate]);

  function InnerCalendar() {
    switch (type) {
      case 'month': return <MonthCalendar {...{ interval, onChange }} defaultYear={value.year} />;
      case 'day': return <DayCalendar {...{ interval, onChange }} defaultDate={value} />;
      case 'year': return <YearCalendar {...{ interval, onChange }} />;
      default: throw Error('Invalid Calendar Type');
    }
  }

  return (
    <div className="CardCalendar">
      <button onClick={closeCalendar} className='calendar-close-btn'>
        <CancelOutlinedIcon className='calendar-icon' />
      </button>

      <InnerCalendar />
    </div>
  );
}

export function MonthCalendar({ defaultYear, interval, onChange }) {
  const [year, setYear] = useState(defaultYear);

  const next = useCallback(() => {
    setYear(y => {
      const newYear = y + 1;
      return newYear <= interval.end.year ? newYear : y;
    });
  }, [interval, setYear]);

  const prev = useCallback(() => {
    setYear(y => {
      const newYear = y - 1;
      return newYear >= interval.start.year ? newYear : y;
    });
  }, [interval, setYear]);

  return (
    <>
      <Selector value={year} onNext={next} onPrev={prev} />
      <MonthGrid onChange={onChange} year={year} interval={interval} />
    </>
  );
}

export function DayCalendar({ defaultDate, interval, onChange }) {
  const [currDate, setCurrDate] = useState(defaultDate);

  const next = useCallback(() => {
    setCurrDate(d => {
      const newDate = d.startOf('month').plus({ months: 1 });
      return interval.contains(newDate) ? newDate : d;
    });
  }, [interval, setCurrDate]);

  const prev = useCallback(() => {
    setCurrDate(d => {
      const newDate = d.minus({ months: 1 });
      return interval.contains(newDate) ? newDate : d;
    });
  }, [interval, setCurrDate]);

  return (
    <>
      <Selector value={currDate.toFormat('MMMM y')} onNext={next} onPrev={prev} />
      <CalendarDays currDate={currDate} interval={interval} onChange={onChange} />
    </>
  );
}

export function YearCalendar({ interval, onChange }) {
  const firstYear = DateTime.local().startOf('year');
  const lastYear = firstYear.minus({ years: 1 }).endOf('year');
  return (
    <div className='calendar-grid year-grid'>
      <button className='full-span' onClick={() => onChange()}>Ultimi 12 mesi</button>

      <div className='grid-divider grid-row-divider' />

      <button
        disabled={!interval.contains(lastYear)}
        onClick={() => onChange(lastYear.startOf('year'))}
      >{lastYear.toFormat('y')}</button>
      <div className='grid-divider' />
      <button
        disabled={!interval.contains(firstYear)}
        onClick={() => onChange(firstYear.startOf('year'))}
      >{firstYear.toFormat('y')}</button>
    </div>
  );
}

function Selector({ value, onPrev, onNext }) {
  return (
    <div className='selector-control'>
      <button onClick={onPrev}><KeyboardArrowLeft className='selector-icon' /></button>
      <p>{value}</p>
      <button onClick={onNext}><KeyboardArrowRight className='selector-icon' /></button>
    </div>
  );
}

function MonthGrid({ year, onChange, interval }) {
  const months = useMemo(() => [...Array(12).keys()].map(i => (
    DateTime.local(year, i+1)
  )), [year]);
  
  return (
    <div className='calendar-grid month-grid'>
      {months.slice(0, 4).map(m => (
        <button
          key={m.toISO()}
          onClick={() => onChange(m)}
          disabled={!interval.contains(m)}
        >{m.toFormat('MMM')}</button>
      )).reduce((prev, curr, i) => [prev, <div className='grid-divider' key={i} />, curr])}

      <div className='grid-divider grid-row-divider' />

      {months.slice(4, 8).map(m => (
        <button
          key={m.toISO()}
          onClick={() => onChange(m)}
          disabled={!interval.contains(m)}
        >{m.toFormat('MMM')}</button>
      )).reduce((prev, curr, i) => [prev, <div className='grid-divider' key={i} />, curr])}

      <div className='grid-divider grid-row-divider' />

      {months.slice(8, 12).map(m => (
        <button
          key={m.toISO()}
          onClick={() => onChange(m)}
          disabled={!interval.contains(m)}
        >{m.toFormat('MMM')}</button>
      )).reduce((prev, curr, i) => [prev, <div className='grid-divider' key={i} />, curr])}
    </div>
  );
}

function CalendarDays({ currDate, interval, onChange }) {
  const [days, startPadding] = useMemo(() => {
    const startOfMonth = currDate.startOf('month');
    const i = Interval.fromDateTimes(startOfMonth, currDate.endOf('month'));
    const startPadding = startOfMonth.weekday - 1;
    return [i.splitBy({days: 1}).map(d => d.start), startPadding];
  }, [currDate]);

  return (
    <div className='calendar-grid days-grid'>
      <p>LUN</p>
      <p>MAR</p>
      <p>MER</p>
      <p>GIO</p>
      <p>VEN</p>
      <p>SAB</p>
      <p>DOM</p>

      {[...Array(startPadding).keys()].map(i => <div key={`day-padding-${i}`} />)}
      {days.map(d => (
        <button 
          key={d.toISO()} 
          className='calendar-day' 
          disabled={!interval.contains(d)}
          onClick={() => onChange(d)}
        >{d.day}</button>
      ))}
    </div>
  );
}

export default CardCalendar;
